import {
  addGeneralMeetingDocuments,
  CleanupFunction,
  DbotEvent,
  DbotEventDispatcher,
  DecisionContext,
  DecisionItem,
  DocumentItem,
  Freetext,
  sortDocuments,
  updateDecisionText,
  WithInitialization,
} from "../common/commonDomain";
import { autorun, makeAutoObservable, runInAction } from "mobx";
import { compact, isEqual, sortBy } from "lodash";
import { Decision } from "../project/projectDomain";
import { FileUploadConfig } from "../../fileUpload/fileUploadDomain";
import {
  CustomizedFreetextType,
  FreetextType,
  LocalizedTextForDocument,
  TranslationOption,
} from "../../common/common";
import { AIClient } from "../../ai/AIAssistant";
import { Language } from "../../i18n/languages";
import { TMessage } from "../../i18n/tt";
import { DecisionType } from "../../common/decisions";
import { DocumentType, isGeneralMeetingDocumentType } from "../../common/documents";
import {
  BooleanFieldModel,
  LocalizedStringFieldModel,
  RadioFieldModel,
} from "../../common/commonForms";
import { LocalizedTextEditorModel } from "../../text/localizedTextEditorDomain";

export type FreetextTypeForDocument = {
  documentType: DocumentType;
  customizedFreetextType: CustomizedFreetextType;
};

export function isDocumentChanged(current: DocumentItem[], context: DecisionContext): boolean {
  const contextDocuments = context.currentDocuments.map((d) => d.documentId);
  const documentIds = current.map((d) => d.documentId);
  return !isEqual(sortBy(documentIds), sortBy(contextDocuments));
}

export function isLanguageChanged(
  documentLanguages: Language[],
  localizedTitles: LocalizedStringFieldModel[],
): boolean {
  return !isEqual(sortBy(documentLanguages), sortBy(localizedTitles.map((t) => t.locale)));
}

export function isDocumentLanguagesChanged(
  current: DocumentItem[],
  descriptionTexts: FreetextDescription[],
) {
  const descriptionDocuments = descriptionTexts.map((d) => d.document);
  return descriptionDocuments.some((desc) =>
    current.some(
      (current) =>
        current.type === desc.type && !isEqual(sortBy(current.languages), sortBy(desc.languages)),
    ),
  );
}

const documentTypesWithDescriptions = [
  DocumentType.notice_board_meeting,
  DocumentType.board_meeting,
  DocumentType.notice_general_meeting,
  DocumentType.general_meeting,
];

export function createDescriptionFields({
  dispatch,
  documents,
  decisionId,
  isReadOnlyInDocument,
  decisionTexts,
  aiClient,
}: {
  dispatch: DbotEventDispatcher;
  documents: DocumentItem[];
  decisionId: string;
  decisionTexts: LocalizedTextForDocument[];
  isReadOnlyInDocument: () => boolean;
  aiClient: AIClient;
}): FreetextDescription[] {
  const relevantDocs = compact(
    documentTypesWithDescriptions.map((type) => documents.find((d) => d.type === type)),
  );

  return relevantDocs.map((document) => {
    const onChange = (translationOption: TranslationOption, language: Language, value: string) => {
      dispatch(updateDecisionText(decisionId, document.type, language, value, translationOption));
    };
    const textModel: LocalizedTextEditorModel = new LocalizedTextEditorModel({
      texts: document.languages?.map(
        (language) =>
          decisionTexts?.find(
            (t) => t.documentType === document.type && t.language === language,
          ) ?? { text: "", language },
      ),
      isReadOnly: () => isReadOnlyInDocument(),
      onChange: onChange,
      label: {
        key: "decisions.decisionTextIn",
        params: { document: document.title },
      },
      aiClient,
    });
    return {
      document,
      textModel,
    };
  });
}

type FreetextDescription = {
  document: DocumentItem;
  textModel: LocalizedTextEditorModel;
};

export type FreetextTypeRadioOptions =
  | FreetextType.DECISION_MATTER
  | FreetextType.INFORMATION_MATTER
  | FreetextType.CUSTOMIZED;

export type FreetextTypeSelection = {
  type: CustomizedFreetextType;
  label: TMessage;
};

export const freetextTypeLabels: { [key in FreetextType]: TMessage } = {
  [FreetextType.INFORMATION_MATTER]: { key: "freetext.informationMatter" },
  [FreetextType.DECISION_MATTER]: { key: "freetext.decisionMatter" },
  [FreetextType.CUSTOMIZED]: { key: "freetext.customized" },
};

const customizedFreetextTypeLabels: { [key in CustomizedFreetextType]: TMessage } = {
  [CustomizedFreetextType.INFORMATION_MATTER]: { key: "freetext.informationMatter" },
  [CustomizedFreetextType.DECISION_MATTER]: { key: "freetext.decisionMatter" },
  [CustomizedFreetextType.EXCLUDED]: { key: "freetext.exclude" },
};

const defaultCustomizedFreetextType = CustomizedFreetextType.INFORMATION_MATTER;
const decisionMatterSelection = CustomizedFreetextType.DECISION_MATTER;
const excludeSelection: CustomizedFreetextType = CustomizedFreetextType.EXCLUDED;

export class FreetextForm implements WithInitialization {
  readonly type = DecisionType.freetext;
  readonly dispatch: DbotEventDispatcher;
  readonly context: DecisionContext;
  readonly decisionId: string;

  localizedTitles: LocalizedTextEditorModel;
  requiresGeneralMeeting: BooleanFieldModel;
  freetextTypeModel?: RadioFieldModel<FreetextType>;
  selectedFreetextType: FreetextType = FreetextType.INFORMATION_MATTER;
  selectedFreetextTypeForBoardMeeting?: CustomizedFreetextType;
  selectedFreetextTypeForGeneralMeeting?: CustomizedFreetextType;
  customizedFreetextTypePerDocument: FreetextTypeForDocument[];
  contextDocuments: DocumentItem[];
  descriptionTexts: FreetextDescription[];

  constructor(
    dispatch: DbotEventDispatcher,
    context: DecisionContext,
    data?: Freetext,
    decision?: Decision,
  ) {
    this.dispatch = dispatch;
    this.context = context;
    this.decisionId = decision?.decisionId ?? "";
    this.contextDocuments = context.currentDocuments;
    this.localizedTitles = new LocalizedTextEditorModel({
      texts: data?.localizedTitles,
      aiClient: context.aiClient,
      isReadOnly: () => context.isLocked,
      required: true,
      label: { key: "decisions.freetext.title" },
    });
    this.localizedTitles.onLanguageChange(context.documentLanguages, data?.localizedTitles);
    this.requiresGeneralMeeting = new BooleanFieldModel({
      value: data?.requiresGeneralMeeting,
      required: true,
    });
    this.selectedFreetextType =
      data?.freetextTypeForAllDocuments || FreetextType.INFORMATION_MATTER;

    this.selectedFreetextTypeForBoardMeeting = data?.customizedFreetextTypePerDocument.find(
      (f) => f.documentType === DocumentType.board_meeting,
    )?.customizedFreetextType;

    this.selectedFreetextTypeForGeneralMeeting = data?.customizedFreetextTypePerDocument.find(
      (f) => f.documentType === DocumentType.general_meeting,
    )?.customizedFreetextType;

    this.freetextTypeModel = this.createFreetextTypeModel(this.context.currentDocuments);
    this.customizedFreetextTypePerDocument = data?.customizedFreetextTypePerDocument ?? [];
    this.descriptionTexts = this.createDescriptionTexts();
    makeAutoObservable(this, { type: false, dispatch: false }, { autoBind: true });
  }

  get decisionData(): Freetext {
    return this.context.currentDecisions.find((d) => d.decisionId === this.decisionId)!
      .decisionData as Freetext;
  }

  get decisionTexts(): LocalizedTextForDocument[] {
    return this.context.decisionTexts;
  }

  createDescriptionTexts() {
    return createDescriptionFields({
      dispatch: this.dispatch,
      documents: this.context.currentDocuments,
      decisionId: this.decisionId,
      decisionTexts: this.decisionTexts,
      aiClient: this.context.aiClient,
      isReadOnlyInDocument: () => this.isReadOnly,
    });
  }
  createFreetextTypeModel(
    documents: DocumentItem[],
  ): RadioFieldModel<FreetextTypeRadioOptions> | undefined {
    if (documents.length === 0) {
      return undefined;
    }
    const projectHasBoardMeeting = documents.some((d) => d.type === DocumentType.board_meeting);
    const projectHasGeneralMeeting = documents.some((d) => d.type === DocumentType.general_meeting);
    const options =
      projectHasBoardMeeting && projectHasGeneralMeeting && this.requiresGeneralMeeting.value
        ? [FreetextType.INFORMATION_MATTER, FreetextType.DECISION_MATTER, FreetextType.CUSTOMIZED]
        : [FreetextType.INFORMATION_MATTER, FreetextType.DECISION_MATTER];

    return new RadioFieldModel<FreetextTypeRadioOptions>({
      value: this.selectedFreetextType,
      options: options,
      idFn: (value) => value,
      labelFn: (value) => freetextTypeLabels[value],
      onChange: (value) => runInAction(() => this.changeFreetextType(value)),
    });
  }

  changeFreetextType(value: FreetextTypeRadioOptions) {
    this.selectedFreetextType = value;
    if (this.isCustomized) {
      this.dispatch({
        type: "dbot/freetext-type-changed",
        decisionId: this.decisionId,
        freetextType: value,
        customizedFreetextType: CustomizedFreetextType.INFORMATION_MATTER,
      });
    } else {
      this.dispatch({
        type: "dbot/freetext-type-changed",
        decisionId: this.decisionId,
        freetextType: value,
      });
    }
  }

  init(): CleanupFunction {
    const cleanupFunctions = [
      autorun(() => {
        this.reactToLanguageChanges();
        this.reactToDocumentChanges();
        this.reactToRequiresGeneralMeetingInvalid();
      }),
    ];
    return () => {
      cleanupFunctions.forEach((f) => f());
    };
  }

  reactToRequiresGeneralMeetingInvalid() {
    if (!this.containsGeneralMeeting && this.requiresGeneralMeeting.value === true) {
      runInAction(() => {
        this.requiresGeneralMeeting.setValue(false);
      });
    }
  }

  reactToDocumentChanges() {
    const documentsChanged = isDocumentChanged(this.contextDocuments, this.context);
    if (documentsChanged) {
      runInAction(() => {
        this.contextDocuments = this.context.currentDocuments;
        this.freetextTypeModel = this.createFreetextTypeModel(this.context.currentDocuments);
        this.descriptionTexts = this.createDescriptionTexts();
      });
    }
  }

  reactToLanguageChanges() {
    runInAction(() => {
      if (isLanguageChanged(this.context.documentLanguages, this.localizedTitles.textModels)) {
        this.localizedTitles.onLanguageChange(
          this.context.documentLanguages,
          this.decisionData.localizedTitles,
        );
      }
      if (isDocumentLanguagesChanged(this.context.currentDocuments, this.descriptionTexts)) {
        this.descriptionTexts = this.createDescriptionTexts();
      }
    });
  }

  handleEvent(event: DbotEvent) {
    switch (event.type) {
      case "dbot/documents-added":
        if (
          event.addedDocumentTypes.some(isGeneralMeetingDocumentType) &&
          !event.existingDocumentTypes.some(isGeneralMeetingDocumentType)
        ) {
          this.requiresGeneralMeeting.setValue(true);
        }
    }
  }

  get showFreetextType(): boolean {
    return this.context.currentDocuments.length > 0;
  }

  selectCustomizedFreetextTypeForDocument(
    selected: CustomizedFreetextType,
    documentType: DocumentType,
  ) {
    if (this.selectedFreetextType === FreetextType.CUSTOMIZED) {
      if (documentType === DocumentType.board_meeting) {
        this.selectedFreetextTypeForBoardMeeting = selected;
      } else if (documentType === DocumentType.general_meeting) {
        this.selectedFreetextTypeForGeneralMeeting = selected;
      }
      this.dispatch({
        type: "dbot/freetext-type-changed",
        decisionId: this.decisionId,
        freetextType: this.selectedFreetextType,
        documentType: documentType,
        customizedFreetextType: selected,
      });
    }
  }

  getSelectedFreetextTypeForDocument(documentType: DocumentType): FreetextTypeSelection {
    let freetextTypeForDocument: CustomizedFreetextType;
    if (documentType === DocumentType.board_meeting) {
      freetextTypeForDocument =
        this.selectedFreetextTypeForBoardMeeting || defaultCustomizedFreetextType;
    } else if (documentType === DocumentType.general_meeting) {
      freetextTypeForDocument =
        this.selectedFreetextTypeForGeneralMeeting || defaultCustomizedFreetextType;
    } else {
      freetextTypeForDocument = defaultCustomizedFreetextType;
    }
    return {
      type: freetextTypeForDocument,
      label: customizedFreetextTypeLabels[freetextTypeForDocument],
    };
  }

  get isCustomized(): boolean {
    return this.selectedFreetextType === FreetextType.CUSTOMIZED;
  }

  get documents(): DocumentItem[] {
    return this.context.currentDocuments.filter(
      (d) => d.type === DocumentType.board_meeting || d.type === DocumentType.general_meeting,
    );
  }

  get freetextTypeOptions(): FreetextTypeSelection[] {
    return [defaultCustomizedFreetextType, decisionMatterSelection, excludeSelection].map(
      (type) => ({
        type: type,
        label: customizedFreetextTypeLabels[type],
      }),
    );
  }

  toCustomizedFreetextTypePerDocument(): FreetextTypeForDocument[] {
    if (this.selectedFreetextType !== FreetextType.CUSTOMIZED) {
      return [];
    } else {
      return [
        {
          documentType: DocumentType.board_meeting,
          customizedFreetextType:
            this.selectedFreetextTypeForBoardMeeting ?? defaultCustomizedFreetextType,
        },
        {
          documentType: DocumentType.general_meeting,
          customizedFreetextType:
            this.selectedFreetextTypeForGeneralMeeting ?? defaultCustomizedFreetextType,
        },
      ];
    }
  }

  get fileUploadConfig(): FileUploadConfig {
    return this.context.fileUploadConfig;
  }

  setRequiresGeneralMeeting(value: boolean) {
    this.requiresGeneralMeeting.setValue(value);
    if (this.requiresGeneralMeeting.value) {
      this.dispatch(addGeneralMeetingDocuments());
    }
    this.freetextTypeModel = this.createFreetextTypeModel(this.context.currentDocuments);
  }

  get currentDocumentTypes(): DocumentType[] {
    const currentDocuments = sortDocuments(this.context.currentDocuments);
    return currentDocuments.map((d) => d.type);
  }

  get isProjectLocked(): boolean {
    return this.context.isLocked;
  }

  get isGeneralMeetingMinutesLocked() {
    const document = this.context.currentDocuments.find(
      (d) => d.type === DocumentType.general_meeting,
    );
    return document !== undefined && this.isLocked(document.documentId);
  }

  get containsGeneralMeeting() {
    return this.currentDocumentTypes.some((type) =>
      [DocumentType.notice_general_meeting, DocumentType.general_meeting].includes(type),
    );
  }

  isLocked(documentId: string) {
    return (
      this.context.documentStatuses.find((it) => it.documentId === documentId)?.isLocked ?? false
    );
  }

  showRequiresGeneralMeeting(documentContext?: DocumentItem): boolean {
    return !documentContext || !isGeneralMeetingDocumentType(documentContext.type);
  }

  validate() {
    return true;
  }

  get isReadOnly() {
    return this.isProjectLocked;
  }
}

export function filterDecisionRequiringVoting(
  decisions: DecisionItem[],
  documentType: DocumentType,
): string[] {
  return decisions
    .filter(
      (d) =>
        d.decisionData?.type !== DecisionType.freetext ||
        (d.decisionData?.type === DecisionType.freetext &&
          d.decisionData.freetextTypeForAllDocuments === FreetextType.DECISION_MATTER) ||
        (d.decisionData?.type === DecisionType.freetext &&
          d.decisionData.customizedFreetextTypePerDocument.some(
            (c) =>
              c.documentType === documentType &&
              c.customizedFreetextType === CustomizedFreetextType.DECISION_MATTER,
          )),
    )
    .map((d) => d.decisionId);
}

const getCustomizedFreetextType = (
  freetext: Freetext,
  documentType: DocumentType,
): CustomizedFreetextType => {
  const adjustDocumentType = (documentType: DocumentType): DocumentType => {
    switch (documentType) {
      case DocumentType.notice_general_meeting:
        return DocumentType.general_meeting;
      case DocumentType.notice_board_meeting:
        return DocumentType.board_meeting;
      default:
        return documentType;
    }
  };
  return (
    freetext.customizedFreetextTypePerDocument.find(
      (x) => adjustDocumentType(x.documentType) === adjustDocumentType(documentType),
    )?.customizedFreetextType ?? CustomizedFreetextType.EXCLUDED
  );
};

/**
 * Duplicates the implementation in Freetext.kt. Keep in sync.
 */
export const showFreetext = (freetext: Freetext, documentType: DocumentType): boolean => {
  const customizedAndExcluded =
    freetext.freetextTypeForAllDocuments === FreetextType.CUSTOMIZED &&
    getCustomizedFreetextType(freetext, documentType) === CustomizedFreetextType.EXCLUDED;

  switch (documentType) {
    case DocumentType.notice_general_meeting:
    case DocumentType.general_meeting:
      return freetext.requiresGeneralMeeting && !customizedAndExcluded;
    default:
      return !customizedAndExcluded;
  }
};
